import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth-store';

const _rawUrl = typeof window === 'undefined'
    ? (process.env.API_URL || 'http://backend:8000')
    : (process.env.NEXT_PUBLIC_API_URL || 'https://api-dev.zava.hu');
// Browser: force HTTPS to prevent Mixed Content errors
const API_URL = typeof window !== 'undefined' && _rawUrl.startsWith('http://')
    ? _rawUrl.replace('http://', 'https://')
    : _rawUrl;

export const api = axios.create({
    baseURL: `${API_URL}/api/v1/`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add access token
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const { accessToken } = useAuthStore.getState();
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // If error is 401 and we haven't retried yet, and NOT a login request
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
            originalRequest._retry = true;

            try {
                const { refreshToken, setTokens, logout } = useAuthStore.getState();

                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                // Call refresh endpoint
                const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
                    refresh_token: refreshToken,
                });

                const { access_token, refresh_token: new_refresh_token } = response.data;

                // Update tokens in store
                setTokens(access_token, new_refresh_token);

                // Update authorization header for original request
                originalRequest.headers.Authorization = `Bearer ${access_token}`;

                // Retry original request
                return api(originalRequest);
            } catch (refreshError) {
                // If refresh fails, logout user
                useAuthStore.getState().logout();

                // Redirect to login (window.location because we are outside React context)
                if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);
